/**
 *
 *  Organizations/Show/CampaignTemplates
 *
 */

import React, { useState, useEffect } from 'react'

// NPM Libraries
import Toggle from 'react-toggle'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from 'date-fns'

// GraphQL
import { gql, useMutation } from '@apollo/client'

// Components
import Form from 'components/Organizations/Show/CampaignTemplates/Form'
import LandingPagePreferences from 'components/Organizations/Show/LandingPagePreferences'
import QueryTable from 'components/Table/Query'
import CampaignsAdditionalQuestions from 'components/Organizations/Show/CampaignsAdditionalQuestions'
import DownloadHistory from 'components/Organizations/Show/CampaignTemplates/DownloadHistory'

import { Campaign } from 'globalVars'
import { notify } from 'utils/notify'

const ToggleContainer = styled.div`
	.react-toggle {
		transform: scale(0.85);
	}
`

const SEARCH_TEMPLATES = gql`
	query searchOrganizationCampaignTemplates(
		$organization_id: ID!
		$page: Int
		$page_size: Int
		$sort: String
		$sort_direction: String
		$filters: JSON
	) {
		search: search_organization_campaign_templates(
			organization_id: $organization_id
			page: $page
			page_size: $page_size
			sort: $sort
			sort_direction: $sort_direction
			filters: $filters
		) {
			_total
			results: campaign_templates {
				id
				name
				topic_to_s
				headline
				objective
				updated_at
				available
			}
		}
	}
`

const REMOVE_CAMPAIGN_TEMPLATE = gql`
	mutation deleteCampaignTemplateFromOrganization($organization_id: ID!, $campaign_template_id: ID!) {
		deleteCampaignTemplateFromOrganization(
			input: { organization_id: $organization_id, campaign_template_id: $campaign_template_id }
		) {
			organization {
				id
				campaign_templates {
					id
					name
				}
			}
		}
	}
`

const UPDATE_CAMPAIGN_TEMPLATE = gql`
	mutation updateCampaignTemplate($id: ID!, $available: Boolean) {
		updateCampaignTemplate(input: { id: $id, available: $available }) {
			campaign_template {
				id
			}
		}
	}
`

const GENERATE_LANDING_PAGE_PREVIEW = gql`
	mutation generateLandingPagePreview($organization_id: ID!, $campaign_template_id: ID!, $downloable: Boolean) {
		generateLandingPagePreview(
			input: {
				organization_id: $organization_id
				campaign_template_id: $campaign_template_id
				downloable: $downloable
			}
		) {
			ready
			campaign_template {
				id
				dummy_campaign_id
				landing_page_preview_url
			}
			report_id
			error
		}
	}
`

const BROADCAST_CAMPAIGN_CHANGE = gql`
	mutation broadcastCampaignChange($campaign_id: ID) {
		broadcastCampaignChange(input: { campaign_id: $campaign_id }) {
			campaign {
				id
			}
		}
	}
`

function CampaignTemplate({ organization }) {
	const [show_template_form, setShowTemplateForm] = useState(null)
	const [show_landing_page_preferences, setShowLandingPagePreferences] = useState(null)
	const [show_additional_questions_section, setShowAdditionalQuestionsSection] = useState(false)
	const [show_download_history, setShowDownloadHistory] = useState(false)
	const [generating_preview, setGeneratingPreview] = useState(false)

	const [updateCampaignTemplate, { loading: updating }] = useMutation(UPDATE_CAMPAIGN_TEMPLATE)
	const [removeCampaignTemplate] = useMutation(REMOVE_CAMPAIGN_TEMPLATE)
	const [broadcastCampaignChange] = useMutation(BROADCAST_CAMPAIGN_CHANGE)

	const [preview] = useMutation(GENERATE_LANDING_PAGE_PREVIEW, {
		onCompleted: ({ generateLandingPagePreview: { campaign_template, error, report_id } }) => {
			setGeneratingPreview(false)

			if (error === 'duplicate') {
				notify({
					message:
						'This Campaign Template is already being downloaded. Please wait until the process finishes.',
				})
				return
			}

			if (report_id) {
				setShowDownloadHistory(true)
			} else {
				broadcastCampaignChange({
					variables: { campaign_id: campaign_template?.dummy_campaign_id },
				}).then(() => {
					window.open(campaign_template?.landing_page_preview_url)
				})
			}
		},
	})

	useEffect(() => {
		const timer = setTimeout(() => setGeneratingPreview(), 50000)

		return () => clearTimeout(timer)
	}, [generating_preview])

	const generatePreview = (template_id, downloable = false) => {
		setGeneratingPreview(true)

		preview({
			variables: {
				organization_id: organization.id,
				campaign_template_id: template_id,
				downloable,
			},
		})
	}

	const columns = [
		{
			Header: 'Publish',
			id: 'available',
			accessor: (row) => (
				<ToggleContainer>
					<Toggle
						value={row.available}
						checked={row.available}
						onChange={() =>
							updateCampaignTemplate({ variables: { id: row.id, available: !row.available } })
						}
						disabled={updating}
					/>
				</ToggleContainer>
			),
			width: 95,
			sortable: false,
		},
		{
			Header: 'Template Name',
			accessor: 'name',
			Cell: (row) => (
				<span
					role="link"
					aria-hidden="true"
					className="btn-link clickable"
					onClick={() => setShowTemplateForm(row)}
				>
					{row.name}
				</span>
			),
		},
		{
			Header: 'Headline',
			accessor: 'headline',
			sortable: false,
		},
		{
			Header: 'Topic',
			id: 'topic',
			accessor: (template) => template.topic_to_s,
			sortable: false,
		},
		{
			Header: 'Objective',
			id: 'objective',
			accessor: (template) => Campaign.OBJECTIVES[template.objective],
			sortable: false,
		},
		{
			Header: 'Last Edited',
			id: 'updated_at',
			accessor: (r) => <React.Fragment>{format(new Date(r.updated_at), 'MMM do, yyyy')}</React.Fragment>,
			width: 140,
		},
		{
			id: 'actions',
			minWidth: 350,
			style: { whiteSpace: 'unset' }, // makes the table scrollable.
			sortable: false,
			accessor: (row) => (
				<div className="text-right">
					<button className="btn btn-sm btn-warning" onClick={() => setShowTemplateForm(row)}>
						Edit
					</button>
					&nbsp;
					<button
						className="btn btn-sm btn-danger"
						onClick={(e) => {
							e.stopPropagation()

							swal({
								title: 'Are you sure you want to delete this Campign Template?',
								type: 'warning',
								showCancelButton: true,
								confirmButtonText: 'Yes, delete this Campaign Template!',
								cancelButtonText: 'Cancel',
								confirmButtonClass: 'btn btn-danger d-inline',
								cancelButtonClass: 'btn btn-default d-inline',
								buttonsStyling: false,
							}).then(
								() =>
									removeCampaignTemplate({
										variables: { campaign_template_id: row.id, organization_id: organization.id },
									}),
								() => {},
							)
						}}
					>
						Delete
					</button>
					&nbsp;
					<button
						className="btn btn-sm btn-info"
						type="button"
						onClick={() => generatePreview(row.id)}
						disabled={generating_preview}
					>
						Preview
					</button>
					&nbsp;
					<button
						className="btn btn-sm btn-purple"
						type="button"
						onClick={() => generatePreview(row.id, true)}
						disabled={generating_preview}
					>
						Download
					</button>
					&nbsp;
					<button
						className="btn btn-sm btn-success"
						type="button"
						onClick={() => setShowTemplateForm({ ...row, copy_template: true })}
					>
						Copy
					</button>
					&nbsp;
				</div>
			),
		},
	]

	return (
		<div>
			<p className="mt-2">
				Create custom Campaign Templates that appear as an option for Users within the Organization. When a
				Template is applied to a Campaign, the information will populate on the Landing Page and is seen by
				prospects as they register.
			</p>
			<p>
				Publishing a Template does not automatically apply it to Campaigns. Instead, Users within the
				Organization will see the Template as an available option within the “Edit Campaign” page.
			</p>
			<p>
				An unpublished Template is not visible to advisor-level Users. The unpublished status can be used while
				Templates are still going through the approval process.
			</p>

			<button type="button" className="btn btn-primary mb-3" onClick={() => setShowTemplateForm({})}>
				<FontAwesomeIcon size="sm" icon="plus" className="mr-1" /> New Campaign Template
			</button>

			<button
				type="button"
				className="btn btn-success float-right mb-3 ml-2"
				onClick={() => setShowAdditionalQuestionsSection(true)}
			>
				<FontAwesomeIcon size="sm" icon="question-circle" className="mr-1" />
				Additional Questions by Campaign Topic
			</button>

			<button
				type="button"
				className="btn btn-success float-right mb-3 ml-2"
				onClick={() => setShowLandingPagePreferences(true)}
			>
				<FontAwesomeIcon size="sm" icon="cog" className="mr-1" /> Landing Page Preferences
			</button>

			<button
				type="button"
				className="btn btn-purple float-right mb-3 ml-2"
				onClick={() => setShowDownloadHistory(true)}
			>
				<FontAwesomeIcon size="sm" icon="download" className="mr-1" />
				Download History
			</button>

			<QueryTable
				query={SEARCH_TEMPLATES}
				columns={columns}
				variables={{ organization_id: organization.id }}
				channels={[organization.id]}
			/>

			{!!show_template_form && (
				<Form
					organization_id={organization.id}
					current_template={show_template_form}
					handleClose={() => setShowTemplateForm(null)}
				/>
			)}

			{!!show_landing_page_preferences && (
				<LandingPagePreferences
					organization_id={organization.id}
					handleClose={() => setShowLandingPagePreferences(null)}
				/>
			)}

			{show_additional_questions_section && (
				<CampaignsAdditionalQuestions
					organization_id={organization.id}
					handleClose={() => setShowAdditionalQuestionsSection(false)}
				/>
			)}

			{show_download_history && (
				<DownloadHistory organization_id={organization.id} handleClose={() => setShowDownloadHistory(false)} />
			)}
		</div>
	)
}

CampaignTemplate.propTypes = {
	organization: PropTypes.object,
}

export default CampaignTemplate
